import { useToast } from "../ui/Toast";

export default function ScreenTooSmall({
  minWidth = 1024,
  title = "Fenêtre trop petite",
  subtitle = `Le jeu nécessite une largeur d'écran suffisante pour afficher les salles et leurs éléments interactifs.`,
}) {
  const toast = useToast();

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Lien copié !");
    } catch {
      toast.error("Copiez l'URL dans la barre du navigateur.");
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#020510",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "2rem",
      fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    }}>
      <div style={{ width: "100%", maxWidth: 480 }}>

        {/* Carte principale */}
        <div style={{
          border: "1px solid rgba(99,102,241,0.4)",
          borderRadius: 16,
          background: "rgba(5,8,30,0.95)",
          overflow: "hidden",
        }}>

          {/* Bande header */}
          <div style={{
            background: "rgba(99,102,241,0.12)",
            borderBottom: "1px solid rgba(99,102,241,0.25)",
            padding: "10px 20px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: "50%",
              background: "#f87171",
              boxShadow: "0 0 0 3px rgba(248,113,113,0.2)",
              flexShrink: 0,
            }} />
            <span style={{
              fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
              color: "rgba(165,180,252,0.65)", fontWeight: 700,
            }}>
              ◈ Projet Origami — Erreur d'affichage
            </span>
          </div>

          <div style={{ padding: "1.75rem" }}>

            {/* Icône + titre */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem", marginBottom: "1.5rem" }}>
              <div style={{
                flexShrink: 0, width: 48, height: 48,
                border: "1px solid rgba(248,113,113,0.35)",
                borderRadius: 10,
                background: "rgba(248,113,113,0.07)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke="#f87171" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <path d="M8 21h8M12 17v4" />
                  <line x1="9" y1="10" x2="10" y2="10" strokeWidth="2.5" />
                  <line x1="14" y1="10" x2="15" y2="10" strokeWidth="2.5" />
                  <circle cx="12" cy="12" r="1" fill="#f87171" stroke="none" />
                </svg>
              </div>
              <div>
                <div style={{
                  fontSize: 17, fontWeight: 700, color: "#fff",
                  marginBottom: 4, fontFamily: "ui-sans-serif, system-ui",
                }}>
                  {title}
                </div>
                <div style={{
                  fontSize: 13, color: "rgba(148,163,184,0.8)", lineHeight: 1.55,
                  fontFamily: "ui-sans-serif, system-ui",
                }}>
                  {subtitle}
                </div>
              </div>
            </div>

            <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", marginBottom: "1.5rem" }} />

            {/* Solution */}
            <div style={{
              background: "rgba(99,102,241,0.08)",
              border: "1px solid rgba(99,102,241,0.3)",
              borderRadius: 10, padding: "1rem 1.25rem", marginBottom: "1rem",
            }}>
              <div style={{
                fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
                color: "rgba(165,180,252,0.55)", marginBottom: 12, fontWeight: 700,
              }}>
                Solution
              </div>

              {[
                {
                  n: 1,
                  title: "Agrandissez la fenêtre",
                  desc: "Faites glisser le bord de la fenêtre pour l'élargir",
                },
                {
                  n: 2,
                  title: "Ou passez en plein écran",
                  desc: null,
                  kbd: "F11",
                  descBefore: "Appuyez sur",
                  descAfter: "pour basculer en plein écran",
                },
              ].map((step) => (
                <div key={step.n} style={{
                  display: "flex", alignItems: "flex-start", gap: 12,
                  marginBottom: step.n === 1 ? 10 : 0,
                }}>
                  <div style={{
                    flexShrink: 0, width: 22, height: 22, borderRadius: "50%",
                    background: "rgba(99,102,241,0.25)",
                    border: "1px solid rgba(99,102,241,0.45)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, color: "rgba(165,180,252,0.9)", fontWeight: 700,
                    marginTop: 1,
                  }}>{step.n}</div>
                  <div>
                    <div style={{
                      fontSize: 13, color: "rgba(226,232,240,0.9)", fontWeight: 600,
                      fontFamily: "ui-sans-serif, system-ui",
                    }}>{step.title}</div>
                    <div style={{
                      fontSize: 12, color: "rgba(148,163,184,0.6)", marginTop: 2,
                      fontFamily: "ui-sans-serif, system-ui",
                    }}>
                      {step.kbd ? (
                        <>
                          {step.descBefore}{" "}
                          <span style={{
                            background: "rgba(255,255,255,0.08)",
                            border: "1px solid rgba(255,255,255,0.15)",
                            borderRadius: 4, padding: "1px 6px",
                            fontSize: 11, color: "rgba(200,210,230,0.85)",
                          }}>{step.kbd}</span>
                          {" "}{step.descAfter}
                        </>
                      ) : step.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Avertissement mobile */}
            <div style={{
              background: "rgba(251,191,36,0.05)",
              border: "1px solid rgba(251,191,36,0.18)",
              borderRadius: 10, padding: "0.7rem 1rem",
              marginBottom: "1.5rem",
              display: "flex", alignItems: "flex-start", gap: 10,
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="rgba(251,191,36,0.65)" strokeWidth="2" strokeLinecap="round"
                style={{ flexShrink: 0, marginTop: 1 }}>
                <path d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
                <circle cx="12" cy="18" r="1" fill="rgba(251,191,36,0.65)" stroke="none" />
              </svg>
              <div style={{
                fontSize: 12, color: "rgba(251,191,36,0.7)", lineHeight: 1.5,
                fontFamily: "ui-sans-serif, system-ui",
              }}>
                Sur mobile ou tablette ? Le jeu est conçu pour ordinateur uniquement. Utilisez un PC ou Mac.
              </div>
            </div>

            {/* Boutons */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                onClick={copyLink}
                style={{
                  width: "100%", padding: "11px 16px", borderRadius: 8,
                  border: "none", background: "rgba(99,102,241,0.85)",
                  color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
                  fontFamily: "ui-monospace, monospace", letterSpacing: "0.05em",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "background 0.15s",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copier le lien — rejoindre depuis un PC
              </button>

              <a
                href="/"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  gap: 6, width: "100%", padding: "10px 16px", borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.1)", background: "transparent",
                  color: "rgba(148,163,184,0.75)", fontSize: 12,
                  fontFamily: "ui-monospace, monospace", textDecoration: "none",
                  boxSizing: "border-box",
                }}
              >
                Retour à l'accueil
              </a>
            </div>

            {/* Requis */}
            <div style={{
              marginTop: "1.25rem", textAlign: "center",
              fontSize: 10, color: "rgba(100,116,139,0.5)", letterSpacing: "0.1em",
            }}>
              LARGEUR MINIMALE REQUISE : {minWidth} PX
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}